// Pure helpers for the published homelab pages (H3a) and the device
// registry (H3b): where a page or device lives in the app, how a link
// inside a page resolves, and the text a due item or device shows. No
// runtime imports.

import type {
  DeviceCategory,
  DeviceIntegration,
  DeviceStatus,
  DueItem,
  HouseDevice,
  HouseSection,
} from './api';

// Each section's pages come from one homelab folder (the publish
// allowlist). A page's route is the section's root + /page/ + its path
// inside that folder without ".md": pets/frank.md is /dogs/page/frank,
// houses/portland-maintenance/laundry-ge.md is
// /home/page/portland-maintenance/laundry-ge.
const SECTIONS: Array<{ section: HouseSection; folder: string; root: string }> = [
  { section: 'home', folder: 'houses/', root: '/home' },
  { section: 'dogs', folder: 'pets/', root: '/dogs' },
  { section: 'car', folder: 'vehicles/', root: '/car' },
];

export const SECTION_ROOT: Record<HouseSection, string> = {
  home: '/home',
  dogs: '/dogs',
  car: '/car',
};

/** The Manuals list. MCP tool descriptions link here. */
export const MANUALS_ROUTE = '/home/manuals';
const MANUALS_FOLDER = 'houses/portland-manuals/';

// What publishing may ever carry (the Worker enforces the same list).
// Used for links while the published-page list hasn't loaded, and for
// files, whose list is per section.
const PAGE_ALLOW = [
  /^houses\/(portland|vancouver)\.md$/,
  /^houses\/portland-maintenance\/[^/]+\.md$/,
  /^pets\/(frank|pepper)\.md$/,
  /^vehicles\/[^/]+\.md$/,
];
const FILE_ALLOW = [
  /^houses\/portland-manuals\/[^/]+\.pdf$/i,
  /^pets\/records\/[^/]+\.pdf$/i,
  /^vehicles\/records\/[^/]+\.pdf$/i,
];

export function isAllowedPage(path: string): boolean {
  return PAGE_ALLOW.some((re) => re.test(path));
}

export function isAllowedFile(path: string): boolean {
  return FILE_ALLOW.some((re) => re.test(path));
}

function encodePath(p: string): string {
  return p.split('/').map(encodeURIComponent).join('/');
}

/** "pets/frank.md", "vaccinations" → "/dogs/page/frank#vaccinations". */
export function pageRoute(path: string, anchor?: string | null): string | null {
  const s = SECTIONS.find((x) => path.startsWith(x.folder));
  if (!s || !path.endsWith('.md')) return null;
  const rest = path.slice(s.folder.length, -3);
  if (!rest) return null;
  const route = `${s.root}/page/${encodePath(rest)}`;
  return anchor ? `${route}#${encodeURIComponent(anchor)}` : route;
}

/** The inverse of pageRoute for a section's `/page/*` splat (React
 *  Router hands it over decoded). */
export function pagePathFromSplat(section: HouseSection, splat: string): string | null {
  const s = SECTIONS.find((x) => x.section === section)!;
  const rest = splat.replace(/^\/+|\/+$/g, '').replace(/\.md$/, '');
  if (!rest || rest.split('/').some((seg) => seg === '' || seg === '.' || seg === '..')) return null;
  return `${s.folder}${rest}.md`;
}

/** "pets/frank.md#vaccinations" → the route, or null if it isn't a page. */
export function pageRefRoute(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const i = ref.indexOf('#');
  const path = i < 0 ? ref : ref.slice(0, i);
  const anchor = i < 0 ? null : safeDecode(ref.slice(i + 1));
  return pageRoute(path, anchor);
}

// ─── links inside a page ─────────────────────────────────────────────

export type LinkTarget =
  | { kind: 'anchor'; anchor: string }
  | { kind: 'route'; to: string }
  | { kind: 'file'; path: string; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'contact'; href: string }
  | { kind: 'dead' };

const REPO = 'https://homelab.invalid/';

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Where a link in the page at `fromPath` goes. Relative links resolve
 * against the page's own path, a leading "/" against the repo root (as
 * on GitHub). `published` is the set of published page paths; null
 * means not loaded yet, and the allowlist stands in for it. A repo link
 * that isn't published is `dead`: there's nothing to open it with.
 */
export function resolveLink(
  href: string,
  fromPath: string,
  published: ReadonlySet<string> | null,
): LinkTarget {
  const raw = href.trim();
  if (!raw) return { kind: 'dead' };
  if (raw.startsWith('#')) return { kind: 'anchor', anchor: safeDecode(raw.slice(1)) };
  let url: URL;
  try {
    url = new URL(raw, REPO + fromPath);
  } catch {
    return { kind: 'dead' };
  }
  if (`${url.protocol}//${url.host}/` !== REPO) {
    if (url.protocol === 'https:' || url.protocol === 'http:') return { kind: 'external', href: url.href };
    if (url.protocol === 'mailto:' || url.protocol === 'tel:') return { kind: 'contact', href: raw };
    return { kind: 'dead' };
  }
  let path = safeDecode(url.pathname.slice(1));
  const anchor = url.hash ? safeDecode(url.hash.slice(1)) : null;
  const isPublished = (p: string) => (published ? published.has(p) : isAllowedPage(p));

  if (path === MANUALS_FOLDER || path === `${MANUALS_FOLDER}README.md`) {
    return { kind: 'route', to: MANUALS_ROUTE };
  }
  if (path === '' || path.endsWith('/')) path += 'README.md';
  if (path.endsWith('.md')) {
    const to = isPublished(path) ? pageRoute(path, anchor) : null;
    return to ? { kind: 'route', to } : { kind: 'dead' };
  }
  if (isAllowedFile(path)) {
    // Keep "#page=4": the browser's PDF viewer opens at that page.
    const fileHref = `/api/house/file?path=${encodeURIComponent(path)}${url.hash}`;
    return { kind: 'file', path, href: fileHref };
  }
  return { kind: 'dead' };
}

/** GitHub's heading anchor: lowercase, punctuation dropped, spaces to "-". */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s_-]/gu, '')
    .replace(/\s/g, '-');
}

// ─── due items ───────────────────────────────────────────────────────

function dayNumber(iso: string): number {
  return Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))) / 86_400_000;
}

/** Whole days from `today` to `iso` (negative when past). */
export function daysBetween(today: string, iso: string): number {
  return Math.round(dayNumber(iso) - dayNumber(today));
}

/** The short "when" for an overdue or due-soon item; null otherwise. */
export function dueWhen(item: DueItem, today: string): string | null {
  if (!item.nextDue || (item.status !== 'overdue' && item.status !== 'due-soon')) return null;
  const d = daysBetween(today, item.nextDue);
  if (item.status === 'overdue') return d === -1 ? '1 day overdue' : d < 0 ? `${-d} days overdue` : 'Overdue';
  if (d <= 0) return 'Today';
  if (d === 1) return 'Tomorrow';
  return `In ${d} days`;
}

/** "every 2 weeks", "every 12 months", "every 6,250 mi", "due at 24,250 mi". */
export function intervalText(item: DueItem): string[] {
  const out: string[] = [];
  const days = item.everyDays;
  if (days) {
    out.push(
      days === 1 ? 'every day' : days === 7 ? 'every week' : days % 7 === 0 ? `every ${days / 7} weeks` : `every ${days} days`,
    );
  } else if (item.everyMonths) {
    out.push(item.everyMonths === 1 ? 'every month' : `every ${item.everyMonths} months`);
  }
  if (item.everyMiles) out.push(`every ${item.everyMiles.toLocaleString('en-US')} mi`);
  if (item.dueMiles) out.push(`due at ${item.dueMiles.toLocaleString('en-US')} mi`);
  return out;
}

/** "2.4 MB", "830 KB". */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Group key for a manual's device: the manifest writes the same device
 *  with and without a trailing note ("LG refrigerator LRFLC2716S
 *  (arriving 2026-10-02)"), so the parenthetical is dropped. */
export function deviceGroup(device: string | null | undefined): string {
  const d = (device ?? '').replace(/\s*\([^()]*\)\s*$/, '').trim();
  return d || 'Other';
}

// ─── devices (H3b) ───────────────────────────────────────────────────

/** The device list on its own. */
export const DEVICES_ROUTE = '/home/devices';

/** "hvac-head-1" → "/home/devices/hvac-head-1". */
export function deviceRoute(id: string): string {
  return `${DEVICES_ROUTE}/${encodeURIComponent(id)}`;
}

/** Group headings, in the order the groups show (house-core's
 *  DEVICE_CATEGORIES order). */
export const CATEGORY_LABEL: Record<DeviceCategory, string> = {
  climate: 'Climate',
  air: 'Air',
  sensor: 'Sensors',
  safety: 'Safety',
  security: 'Security',
  access: 'Doors and access',
  cleaning: 'Cleaning',
  kitchen: 'Kitchen',
  laundry: 'Laundry',
  water: 'Water',
  lighting: 'Lighting',
  network: 'Network',
  printer: 'Printers',
  outdoor: 'Outdoor',
  hub: 'Hub',
  other: 'Other',
};

export const CATEGORY_ICON: Record<DeviceCategory, string> = {
  climate: 'thermometer',
  air: 'wind',
  sensor: 'sensor',
  safety: 'shield',
  security: 'camera',
  access: 'lock',
  cleaning: 'vacuum',
  kitchen: 'dining',
  laundry: 'washer',
  water: 'droplet',
  lighting: 'bulb',
  network: 'wifi',
  printer: 'printer',
  outdoor: 'sun',
  hub: 'chip',
  other: 'plug',
};

export const INTEGRATION_LABEL: Record<DeviceIntegration, string> = {
  'apple-home': 'Apple Home',
  'home-assistant': 'Home Assistant',
  'vendor-app': 'Maker’s app',
  none: 'None',
};

/** Tag text for a device that isn't simply installed; null when it is. */
export const STATUS_LABEL: Record<DeviceStatus, string | null> = {
  installed: null,
  ordered: 'Ordered',
  planned: 'Planned',
  retired: 'Retired',
};

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2019" → "2019", "2019-06" → "Jun 2019", "2019-06-03" → "Jun 3, 2019". */
export function partialDateText(s: string | null | undefined): string | null {
  if (!s) return null;
  const [y, m, d] = s.split('-');
  if (!m) return y;
  const mon = MONTH[Number(m) - 1] ?? m;
  return d ? `${mon} ${Number(d)}, ${y}` : `${mon} ${y}`;
}

/** The last day a partial date covers: "2027" → "2027-12-31", "2027-02" → "2027-02-28". */
export function partialDateEnd(s: string): string {
  const [y, m, d] = s.split('-');
  if (d) return s;
  if (!m) return `${y}-12-31`;
  const last = new Date(Date.UTC(Number(y), Number(m), 0)).getUTCDate();
  return `${y}-${m}-${String(last).padStart(2, '0')}`;
}

/** "GE GFD45ESSK0WW", or whichever half is known. */
export function brandModel(d: Pick<HouseDevice, 'brand' | 'model'>): string | null {
  return [d.brand, d.model].filter(Boolean).join(' ') || null;
}

/** The due item a device's badge shows: its first overdue or due-soon
 *  item (items arrive sorted by next due), else null. */
export function urgentDue(items: DueItem[]): DueItem | null {
  return items.find((i) => i.status === 'overdue') ?? items.find((i) => i.status === 'due-soon') ?? null;
}

/** Devices by category, in CATEGORY_LABEL order; within a group the
 *  published order, with retired devices last. */
export function groupDevices(devices: HouseDevice[]): Array<{ category: DeviceCategory; devices: HouseDevice[] }> {
  return (Object.keys(CATEGORY_LABEL) as DeviceCategory[])
    .map((category) => ({
      category,
      devices: devices
        .filter((d) => d.category === category)
        .sort((a, b) => Number(a.status === 'retired') - Number(b.status === 'retired')),
    }))
    .filter((g) => g.devices.length > 0);
}

/** "2 manuals", "1 manual". */
export function manualsText(n: number): string | null {
  if (n === 0) return null;
  return n === 1 ? '1 manual' : `${n} manuals`;
}
